import type { Meeting, MeetingSchedule, MeetingType } from '@/modules/meetings/types';
import {
  buildRRule, dtstartFor, expandOccurrences, nextOccurrence, parseRRule, type ScheduleRule,
} from '@/modules/meetings/meeting-schedules';
import {
  buildMeetingInstance, rosterAfterHostChange, seriesOverrides,
} from '@/modules/meetings/meeting-instance';
import { rosterKey } from '@/modules/meetings/series-divergence';

/**
 * What a series edit does to the occurrences already materialized from it.
 *
 * "All meetings" in the scope prompt. There is no "this and following" and no
 * schedule splitting: every occurrence is materialized before it happens, so a
 * past one already carries a run status and the predicate below excludes it.
 * What remains is exactly the set a calendar app would call "this and
 * following".
 *
 * A row is left alone when it *diverged* — `scheduledAt` moved off
 * `occurrenceStart` — not because a flag says so. That is the whole record
 * that someone moved it on purpose, and it survives this edit.
 */

/** The horizon the materializer maintains, so a plan does not reach past it. */
const WINDOW_DAYS = 28;
const MS_PER_DAY = 86_400_000;

export interface SeriesPropagation {
  /** Undiverged rows moved to the rule's new instants, both timestamps together. */
  rewrite: { id: string; occurrenceStart: string; scheduledAt: string }[];
  /** Undiverged rows the new rule no longer produces. Not history — they never ran. */
  remove: string[];
  /** Instants the new rule produces that nothing holds yet. */
  create: string[];
}

/**
 * What a change to a series' host, roster or prep settings does to the
 * occurrences already materialized from it.
 *
 * Unlike timing, these are copied onto each meeting at materialization, so
 * writing the schedule alone reaches nothing already on the calendar. Carried
 * field by field, and only onto meetings that still agree with the old series
 * value — an occurrence given its own host keeps it while its roster follows.
 */
export interface FieldPropagation {
  id: string;
  updates: Partial<Meeting>;
}

/** What the materializer would copy onto a meeting of this type and series. */
const baselineOf = (
  type: MeetingType, schedule: MeetingSchedule | null, tz: string, now: string,
): Meeting => buildMeetingInstance({
  id: 'baseline', type, scheduledAt: now, workspaceTz: tz,
  cycle: null, quarterlyCycles: null, actingPersonId: null,
  occurrence: schedule ? { scheduleId: schedule.id, start: now } : null,
  overrides: schedule ? seriesOverrides(schedule) : {},
});

/** Ahead of us and still to run. A meeting that already happened keeps what it had. */
function stillToCome(meeting: Meeting, nowMs: number): boolean {
  if (meeting.status !== 'scheduled') return false;
  const at = meeting.occurrenceStart ?? meeting.scheduledAt;
  return at != null && new Date(at).getTime() >= nowMs;
}

/**
 * What one meeting takes from the change, or `null` for nothing.
 *
 * Field by field: a meeting keeps any value it no longer shares with the old
 * baseline, which is how a customized occurrence survives an edit to what it
 * came from.
 */
function carriedUpdates(was: Meeting, rebuilt: Meeting, meeting: Meeting): Partial<Meeting> | null {
  const host = (was.hostId ?? null) !== (rebuilt.hostId ?? null);

  // A stored roster never held the host, so rebuilding alone would uninvite the
  // outgoing one.
  const is: Meeting = host
    ? {
        ...rebuilt,
        invitees: rosterAfterHostChange(
          rebuilt.invitees, was.hostId, rebuilt.hostId) as string[],
      }
    : rebuilt;

  // `null`, not omitted: `updateMeeting` skips an `undefined` key.
  const updates: Partial<Meeting> = {};
  if (host && (meeting.hostId ?? null) === (was.hostId ?? null)) {
    updates.hostId = (is.hostId ?? null) as string;
  }
  if (rosterKey(was.invitees) !== rosterKey(is.invitees)
    && rosterKey(meeting.invitees) === rosterKey(was.invitees)) {
    updates.invitees = is.invitees;
  }
  if ((was.prepEmailEnabled ?? null) !== (is.prepEmailEnabled ?? null)
    && (meeting.prepEmailEnabled ?? null) === (was.prepEmailEnabled ?? null)) {
    updates.prepEmailEnabled = is.prepEmailEnabled ?? null;
  }
  if ((was.notificationTimingHours ?? null) !== (is.notificationTimingHours ?? null)
    && (meeting.notificationTimingHours ?? null) === (was.notificationTimingHours ?? null)) {
    updates.notificationTimingHours = (is.notificationTimingHours ?? null) as number;
  }
  return Object.keys(updates).length ? updates : null;
}

export function planFieldPropagation(
  before: MeetingSchedule,
  after: MeetingSchedule,
  /** The effective type, for the defaults a null series column inherits. */
  type: MeetingType,
  meetings: Meeting[],
  tz: string,
  now: string,
): FieldPropagation[] {
  // One baseline each, not one per meeting: these fields are the same for every
  // occurrence, so the instant passed in does not matter.
  const was = baselineOf(type, before, tz, now);
  const rebuilt = baselineOf(type, after, tz, now);
  const nowMs = new Date(now).getTime();
  const out: FieldPropagation[] = [];

  for (const meeting of meetings) {
    if (meeting.meetingScheduleId !== after.id) continue;
    if (!stillToCome(meeting, nowMs)) continue;
    const updates = carriedUpdates(was, rebuilt, meeting);
    if (updates) out.push({ id: meeting.id, updates });
  }

  return out;
}

/**
 * The same carry, one rung up: a change to the type's host, roster or prep
 * settings reaching the meetings it already produced.
 *
 * A series occurrence is compared through its own schedule, so a series that
 * names its own host is untouched by the type changing one. One-off meetings of
 * the type are included — they were built from it too.
 *
 * Scoped to the meetings the caller holds. A child team inheriting this type
 * has its own rows, which this cannot see and does not claim to reach.
 */
export function planTypeFieldPropagation(
  before: MeetingType,
  after: MeetingType,
  meetings: Meeting[],
  schedules: MeetingSchedule[],
  tz: string,
  now: string,
): FieldPropagation[] {
  const nowMs = new Date(now).getTime();
  const byId = new Map(schedules.map((s) => [s.id, s]));
  const out: FieldPropagation[] = [];

  for (const meeting of meetings) {
    if (meeting.meetingType !== after.key) continue;
    if (!stillToCome(meeting, nowMs)) continue;
    const schedule = meeting.meetingScheduleId
      ? byId.get(meeting.meetingScheduleId) ?? null : null;
    const updates = carriedUpdates(
      baselineOf(before, schedule, tz, now),
      baselineOf(after, schedule, tz, now),
      meeting,
    );
    if (updates) out.push({ id: meeting.id, updates });
  }

  return out;
}

export function planSeriesPropagation(
  schedule: MeetingSchedule,
  meetings: Meeting[],
  tz: string,
  now: string,
): SeriesPropagation {
  const mine = meetings.filter((m) => m.meetingScheduleId === schedule.id && m.occurrenceStart);
  const nowMs = new Date(now).getTime();

  // A past occurrence still marked scheduled is a meeting the team missed, not
  // a slot waiting to be filled. Dragging it into a future slot would lose the
  // fact that it was missed, which the list reads from its date.
  const movable = mine
    .filter((m) => m.status === 'scheduled'
      && m.scheduledAt === m.occurrenceStart
      && new Date(m.occurrenceStart!).getTime() >= nowMs)
    .sort((a, b) => a.occurrenceStart!.localeCompare(b.occurrenceStart!));

  // Anything else of this schedule's — diverged, run, or missed — still owns
  // its instant. The occurrence index is unique per schedule, so a rewrite onto
  // one of those would be refused by the database.
  const held = new Set(
    mine.filter((m) => !movable.includes(m))
      .map((m) => new Date(m.occurrenceStart!).getTime()),
  );

  const windowEnd = new Date(nowMs + WINDOW_DAYS * MS_PER_DAY).toISOString();
  const occurrences = new Set(expandOccurrences(schedule, tz, { from: now, to: windowEnd }));
  const next = nextOccurrence(schedule, tz, now);
  if (next) occurrences.add(next);

  const wanted = [...occurrences]
    .filter((iso) => !held.has(new Date(iso).getTime()))
    .sort();

  const rewrite: SeriesPropagation['rewrite'] = [];
  const remove: string[] = [];

  movable.forEach((meeting, i) => {
    const target = wanted[i];
    if (target === undefined) {
      remove.push(meeting.id);
      return;
    }
    if (target !== meeting.occurrenceStart) {
      // Both together. Writing only `scheduledAt` would leave the pair unequal
      // and mark every instance in the series as deliberately moved, freezing
      // it against the next edit.
      rewrite.push({ id: meeting.id, occurrenceStart: target, scheduledAt: target });
    }
  });

  return { rewrite, remove, create: wanted.slice(movable.length) };
}

/**
 * The occurrences ending a series asks about: still to run, and moved off
 * their slot on their own. One that ran or was missed is history and is never
 * offered for deletion.
 */
export function movedOccurrencesAhead(
  scheduleId: string,
  meetings: Meeting[],
  now: string,
): Meeting[] {
  const nowMs = new Date(now).getTime();
  return meetings.filter((m) => m.meetingScheduleId === scheduleId
    && m.status === 'scheduled'
    && m.occurrenceStart && m.scheduledAt
    && m.scheduledAt !== m.occurrenceStart
    && new Date(m.scheduledAt).getTime() >= nowMs);
}

/**
 * The same series, re-anchored to a new instant.
 *
 * "All meetings" after dragging one occurrence: a calendar re-reads the rule
 * from where the meeting was put, so moving a Monday to a Thursday moves the
 * series to Thursdays rather than only taking the new time of day. The cadence
 * and interval are the team's decision and survive; the anchor is not.
 */
export function reanchorSchedule(
  schedule: MeetingSchedule,
  instant: string,
  tz: string,
): { dtstart: string; rrule: string } | null {
  const spec = parseRRule(schedule.rrule);
  if (!spec) return null;

  const wall = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(instant));
  const part = (type: string) => wall.find((p) => p.type === type)?.value ?? '';
  const dayOfMonth = Number(part('day'));
  const month = Number(part('month')) - 1;
  const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(part('weekday'));
  const date = `${part('year')}-${part('month')}-${part('day')}`;
  const time = `${String(Number(part('hour')) % 24).padStart(2, '0')}:${part('minute')}`;

  const rule: ScheduleRule = spec.rule.freq === 'weekly'
    ? { ...spec.rule, byWeekday: [dayOfWeek] }
    : spec.rule.freq === 'yearly'
      ? { ...spec.rule, month, dayOfMonth }
      : {
          ...spec.rule,
          // An anchor by weekday keeps being one — "the third Tuesday" moved to
          // another Tuesday is still an nth-weekday rule, not a date.
          ...(spec.rule.on === 'weekday'
            ? { on: 'weekday' as const, nth: nthWeekdayOf(dayOfMonth), dayOfWeek }
            : { on: 'day' as const, dayOfMonth }),
        };

  return {
    dtstart: dtstartFor(date, time, tz),
    rrule: buildRRule({ rule, until: spec.until }),
  };
}

/** Which occurrence of its weekday a date is, counting from the 1st. */
function nthWeekdayOf(dayOfMonth: number): 1 | 2 | 3 | 4 | -1 {
  const nth = Math.ceil(dayOfMonth / 7);
  return (nth >= 5 ? -1 : nth) as 1 | 2 | 3 | 4 | -1;
}
